'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Building2, Globe, MapPin, Phone } from 'lucide-react';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { formatBusinessLocationShort } from '@/lib/format-address';
import {
  WhatsAppIcon,
  WHATSAPP_BUTTON_SIZE_CLASS,
  WHATSAPP_ICON_IN_BUTTON_CLASS,
  whatsappIconButtonClassName,
} from '@/components/icons';
import { formatDisplayPhone, phoneTelHref, whatsappPhoneHref } from '@/lib/phone-utils';
import { cn } from '@/lib/utils';
import { SocialLinks } from '@/components/social/social-links';
import type { BusinessDirectorySort, CommunityBusiness } from '@/types';

const cardClassName = cn(
  'group rounded-2xl',
  'bg-zinc-50 dark:bg-card',
  'border border-zinc-300 dark:border-border/80',
  'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_14px_rgba(15,23,42,0.08)]',
  'dark:shadow-md dark:shadow-black/25',
  'ring-1 ring-zinc-900/5 dark:ring-border/50'
);

interface BusinessDirectoryCardProps {
  business: CommunityBusiness;
  sort?: BusinessDirectorySort;
  onNavigate: (businessId: string) => void;
}

export function BusinessDirectoryCard({
  business,
  sort = 'business',
  onNavigate,
}: BusinessDirectoryCardProps) {
  const locationLine = formatBusinessLocationShort({
    address: business.address,
    addressJson: business.addressJson,
  });
  const categoryLabel = formatBusinessCategoryLabel(
    business.category,
    business.subCategory,
    business.selfDefinedCategory
  );
  const whatsappHref = whatsappPhoneHref(business.phone);
  const ownerName = business.owner.name?.trim() || '';
  const primaryTitle = sort === 'name' ? ownerName || business.name : business.name;
  const secondaryTitle =
    sort === 'name'
      ? ownerName && business.name && ownerName !== business.name
        ? business.name
        : null
      : ownerName && ownerName !== business.name
        ? ownerName
        : null;

  return (
    <Card
      className={cn(
        cardClassName,
        'h-full cursor-pointer transition-all duration-300',
        'hover:border-zinc-400 hover:shadow-[0_2px_6px_rgba(15,23,42,0.08),0_8px_24px_rgba(15,23,42,0.1)]',
        'dark:hover:border-primary/45 dark:hover:shadow-lg dark:hover:shadow-black/35'
      )}
      onClick={() => onNavigate(business.id)}
    >
      <CardHeader className="space-y-0 p-3 pb-1">
        <CardTitle className="flex items-center gap-2.5">
          <Avatar className="h-9 w-9 ring-2 ring-primary/10 transition-transform group-hover:scale-105 shrink-0">
            <AvatarImage src={business.logo} className="object-cover" />
            <AvatarFallback className="bg-primary/5">
              <Building2 className="h-5 w-5 text-primary" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0 gap-0.5">
            <span className="font-semibold text-base leading-tight truncate">{primaryTitle}</span>
            {secondaryTitle ? (
              <span className="text-xs text-muted-foreground truncate">{secondaryTitle}</span>
            ) : null}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-0 space-y-1">
        {categoryLabel ? (
          <p className="text-xs font-medium text-foreground leading-snug">{categoryLabel}</p>
        ) : null}
        {locationLine ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
            <span className="truncate">{locationLine}</span>
          </div>
        ) : null}
        {(business.phone || business.website || whatsappHref) && (
          <div className="flex items-center justify-between gap-2">
            {business.phone ? (
              <a
                href={phoneTelHref(business.phone)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{formatDisplayPhone(business.phone)}</span>
              </a>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1.5 shrink-0">
              {business.website ? (
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
                  title="Visit website"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span className="sr-only">Visit website</span>
                </a>
              ) : null}
              {whatsappHref ? (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(whatsappIconButtonClassName, WHATSAPP_BUTTON_SIZE_CLASS)}
                  title={`WhatsApp ${business.owner.name || business.name}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <WhatsAppIcon className={WHATSAPP_ICON_IN_BUTTON_CLASS} />
                  <span className="sr-only">WhatsApp</span>
                </a>
              ) : null}
              {business.socialLinks && Object.keys(business.socialLinks).length > 0 ? (
                <SocialLinks socialLinks={business.socialLinks} size="sm" />
              ) : null}
            </div>
          </div>
        )}
        {business.workArea ? (
          <p className="text-[11px] text-muted-foreground leading-snug">
            <span className="text-muted-foreground/80">Service area:</span>{' '}
            <span className="font-medium text-primary">{business.workArea}</span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Placeholder card used by phantom-ui to generate skeleton shapes. */
export function BusinessDirectoryCardPlaceholder() {
  return (
    <Card className={`${cardClassName} h-full`}>
      <CardHeader className="space-y-0 p-3 pb-1">
        <CardTitle className="flex items-center gap-2.5">
          <Avatar
            className="h-9 w-9 ring-2 ring-primary/10 shrink-0"
            data-shimmer-width={40}
            data-shimmer-height={40}
          >
            <AvatarFallback className="bg-primary/5">
              <Building2 className="h-5 w-5 text-primary opacity-0" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            <span className="font-semibold text-base leading-tight">Placeholder Business Name</span>
            <span className="text-xs text-muted-foreground">Registered owner name</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-0 space-y-1">
        <p className="text-xs font-medium text-foreground">Business category label</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 shrink-0 opacity-0" />
          <span>Sample City</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium">+91 98765 43210</span>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="h-7 w-7 rounded-full bg-muted/50" />
            <span className={cn(WHATSAPP_BUTTON_SIZE_CLASS, 'rounded-full bg-[#25D366]/30')} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
